import React, {useState} from 'react';

import {useMutation, useQuery} from '@apollo/client/react';
import {AlertCircle, Tag} from 'lucide-react';
import {ActiveSubscription, SeatUsage, SubscriptionOffer} from '../../types/graphql';
import {useLoginInfo} from '../../graphql/localState';
import {Pill} from '../SubscriptionBadges';
import {formatDate, formatMoney, isInvoiced, MODULE_LABELS, paymentTermsLabel, TIER_LABELS} from '../../utils/subscriptions';
import {
  describeOfferDiscount,
  formatOfferTotals,
  lineListAmount,
  offerTargetOf,
  offerTotals,
  OFFER_STATUS_LABELS,
  OFFER_STATUS_TONES,
} from '../../utils/subscriptionOffers';
import {SubscriptionOfferModal} from '../SubscriptionOfferModal';
import {gql} from 'types/__generated__';

export const SUBSCRIPTION_OFFERS_QUERY = gql(`
  query OrganisationSubscriptionOffers($id: UUID) {
    organisation(id: $id) {
      id
      subscriptionOffers {
        id
        status
        trialDays
        paymentTermsDays
        validUntil
        note
        createdAt
        acceptedAt
        subscriptionId
        appliedToExisting
        appliesToSubscriptionId
        closedAt
        closedReason
        globalDiscount {
          percentOff
          durationMonths
        }
        lines {
          productId
          quantity
          discount {
            percentOff
            durationMonths
          }
          product {
            id
            name
            module
            tier
            quantity
            price {
              amount
              currency
            }
          }
        }
        createdBy {
          id
          name
        }
        acceptedBy {
          id
          name
        }
        closedBy {
          id
          name
        }
      }
      activeSubscription {
        id
        isActive
        isCwayTrial
        autoRenewal
        paymentTermsDays
        discount {
          percentOff
          endsAt
        }
        products {
          id
          name
          quantity
          module
          tier
          addon
          seatType
          price {
            amount
            currency
          }
          discount {
            percentOff
            endsAt
          }
        }
      }
      seatUsage(seatType: PROJECT) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
    }
  }
`);

/** What the history query carries of the running subscription: enough to prefill the builder. */
type OfferSubscription = Pick<
  ActiveSubscription,
  'id' | 'isActive' | 'isCwayTrial' | 'autoRenewal' | 'discount' | 'products' | 'paymentTermsDays'
>;

interface OffersResult {
  organisation: {
    id: string;
    subscriptionOffers: SubscriptionOffer[];
    activeSubscription: OfferSubscription | null;
    seatUsage: SeatUsage | null;
  } | null;
}

export const REVOKE_SUBSCRIPTION_OFFER = gql(`
  mutation RevokeSubscriptionOffer($organisationId: UUID!, $offerId: UUID!, $reason: String) {
    revokeSubscriptionOffer(organisationId: $organisationId, offerId: $offerId, reason: $reason)
  }
`);

interface SubscriptionOffersProps {
  organisationId: string;
  organisationName: string;
  /**
   * The organisation page nests this under a tab heading; the subscription modal's own sections
   * sit one level higher. Same styling either way — only the outline changes.
   */
  headingLevel?: 'h3' | 'h4';
}

const primaryButtonClass =
  'px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors';
const dangerButtonClass = 'px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors';
const subtleButtonClass =
  'px-3 py-1.5 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors';

/** Epoch millis with the time of day, for the audit lines under an offer. */
function formatMoment(value: number | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface OfferCardProps {
  offer: SubscriptionOffer;
  busy: boolean;
  onRevoke: (offer: SubscriptionOffer) => void;
  /** The subscription an open offer changes; only read for the retention badge. */
  target: OfferSubscription | null;
}

function OfferCard({offer, busy, onRevoke, target}: OfferCardProps) {
  const totals = offerTotals(offer);
  const changesSubscription = offer.status === 'OPEN' && !!offer.appliesToSubscriptionId;
  const terms = paymentTermsLabel(offer.paymentTermsDays);
  // Terms on an offer to a card-paying organisation move that subscription onto invoicing when
  // it is accepted; one already invoiced is only having the same arrangement restated.
  const switchesToInvoicing = changesSubscription && !!terms && !!target && !isInvoiced(target);
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={OFFER_STATUS_TONES[offer.status]}>{OFFER_STATUS_LABELS[offer.status]}</Pill>
        {changesSubscription && (
          <Pill tone="purple" title={`Applies to subscription ${offer.appliesToSubscriptionId}`}>
            changes current subscription
          </Pill>
        )}
        {changesSubscription && target && !target.autoRenewal && (
          <Pill tone="amber" title="Renewal is cancelled today; accepting the offer turns it back on">
            retention: reinstates renewal
          </Pill>
        )}
        {terms && (
          <Pill tone="teal" title="Accepted with billing details instead of a card; Stripe sends each invoice">
            {terms}
          </Pill>
        )}
        <span className="text-sm text-gray-600 dark:text-gray-300">
          Made {formatMoment(offer.createdAt)} by {offer.createdBy?.name || 'unknown'}
        </span>
        {offer.status === 'OPEN' && (
          <button onClick={() => onRevoke(offer)} disabled={busy} className={`${dangerButtonClass} ml-auto`}>
            Revoke
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
        <table className="w-full text-left text-sm">
          <thead className="bg-white dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Module</th>
              <th className="px-3 py-2 font-medium text-right">Qty</th>
              <th className="px-3 py-2 font-medium text-right">List / month</th>
              <th className="px-3 py-2 font-medium">Deal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
            {offer.lines.map((line) => {
              const listAmount = lineListAmount(line);
              const deal = describeOfferDiscount(line.discount);
              return (
                <tr key={line.productId}>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                    {line.product ? (
                      line.product.name
                    ) : (
                      <span className="font-mono text-xs" title="This product no longer exists in Stripe">
                        {line.productId}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {line.product?.module ? MODULE_LABELS[line.product.module] : '—'}
                    {line.product?.tier ? ` · ${TIER_LABELS[line.product.tier]}` : ''}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right">{line.quantity}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right whitespace-nowrap">
                    {listAmount !== null && line.product ? formatMoney(listAmount, line.product.price.currency) : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {deal ? <span className="text-green-700 dark:text-green-400">{deal}</span> : <span className="text-gray-400">list price</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 pt-1">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Discount on everything</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{describeOfferDiscount(offer.globalDiscount) || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Free trial</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{offer.trialDays ? `${offer.trialDays} days` : '—'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Payment terms</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{offer.paymentTermsDays ? `Net ${offer.paymentTermsDays}` : 'Card'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Accept before</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{offer.validUntil ? formatDate(offer.validUntil) : 'No deadline'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">First month</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {formatOfferTotals(totals)}
            {totals[0]?.incomplete ? ' +' : ''}
          </p>
        </div>
      </div>

      {offer.note && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <span className="text-gray-500 dark:text-gray-400">Note: </span>
          {offer.note}
        </p>
      )}

      {changesSubscription && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          On acceptance this replaces the lines and discounts of subscription{' '}
          <span className="font-mono break-all">{offer.appliesToSubscriptionId}</span>, prorated and invoiced at once.
          {switchesToInvoicing &&
            ` It also switches that subscription to invoicing, net ${offer.paymentTermsDays} — the card on file stops being charged.`}
        </p>
      )}
      {terms && offer.status === 'OPEN' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The customer accepts this with company name, address and VAT id instead of a card. Stripe then sends every invoice, payable within{' '}
          {offer.paymentTermsDays} days by bank transfer or card on the hosted invoice.
        </p>
      )}
      {offer.status === 'ACCEPTED' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Accepted {formatMoment(offer.acceptedAt)}
          {offer.acceptedBy ? ` by ${offer.acceptedBy.name}` : ''}
          {offer.subscriptionId ? ` — ${offer.appliedToExisting ? 'applied to existing subscription' : 'created subscription'} ` : ''}
          {offer.subscriptionId && <span className="font-mono break-all">{offer.subscriptionId}</span>}
        </p>
      )}
      {offer.status === 'REVOKED' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Revoked {formatMoment(offer.closedAt)}
          {offer.closedBy ? ` by ${offer.closedBy.name}` : ''}
          {offer.closedReason ? ` — ${offer.closedReason}` : ''}
        </p>
      )}
      {offer.status === 'EXPIRED' && <p className="text-xs text-gray-500 dark:text-gray-400">The deadline passed without the customer accepting.</p>}
    </div>
  );
}

/**
 * The sales offer builder and its history for one organisation. It runs its own query so the
 * organisation page does not have to carry the offer selection set, and so a permission error on
 * the system-admin-only `subscriptionOffers` field cannot blank the rest of the page.
 */
export default function SubscriptionOffers({organisationId, organisationName, headingLevel = 'h4'}: SubscriptionOffersProps) {
  // Offers are a system-admin feature end to end: the history field returns nothing to anyone
  // else and both mutations refuse them, so there is nothing here for a non-admin to look at.
  const isSystemAdmin = useLoginInfo()?.isSystemAdmin === true;
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [revoking, setRevoking] = useState<SubscriptionOffer | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery<OffersResult>(SUBSCRIPTION_OFFERS_QUERY, {
    variables: {id: organisationId},
    fetchPolicy: 'cache-and-network',
    skip: !isSystemAdmin,
  });
  const [revokeOffer, {loading: revokeBusy}] = useMutation(REVOKE_SUBSCRIPTION_OFFER);

  const offers = data?.organisation?.subscriptionOffers ?? [];
  const openOffer = offers.find((offer) => offer.status === 'OPEN') ?? null;
  /**
   * A paid subscription makes the next offer a change to it rather than a new one: the builder
   * prefills from it and the server applies the offer to it on acceptance. A CWAY trial is not
   * one — the offer then creates the subscription, as before.
   */
  const target = offerTargetOf(data?.organisation?.activeSubscription);
  const seatUsage = data?.organisation?.seatUsage ?? null;

  const confirmRevoke = async () => {
    if (!revoking) return;
    setError('');
    setSuccess('');
    try {
      await revokeOffer({variables: {organisationId, offerId: revoking.id, reason: revokeReason.trim() || null}});
    } catch (err: any) {
      setError(err.message || 'The offer could not be revoked');
      return;
    }
    // The offer is withdrawn from here on: a refresh that fails afterwards is a stale view,
    // never a reason to report the revoke as failed and invite a second attempt.
    setRevoking(null);
    setRevokeReason('');
    setSuccess('Offer revoked');
    try {
      await refetch();
    } catch {
      setSuccess('Offer revoked. The list could not be refreshed — reload to see the current state.');
    }
  };

  if (!isSystemAdmin) return null;

  const Heading = headingLevel;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <Heading className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Subscription offers</Heading>
        <button
          onClick={() => setIsBuilderOpen(true)}
          disabled={!!openOffer}
          className={primaryButtonClass}
          title={openOffer ? 'Revoke the open offer first — only one may be open at a time' : undefined}
        >
          {target ? 'Offer a change' : 'Make offer'}
        </button>
      </div>

      <div className="space-y-3">
        {(error || queryError) && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {error || queryError?.message}
          </div>
        )}
        {success && !error && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
            {success}
          </div>
        )}

        {revoking && (
          <div className="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
            <span className="flex-1 min-w-[200px]">
              Withdraw this offer? {organisationName} can no longer accept it, and a replacement has to be built from scratch.
            </span>
            <input
              type="text"
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.target.value)}
              placeholder="Reason (optional)"
              className="px-3 py-1.5 border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
            <button onClick={confirmRevoke} disabled={revokeBusy} className={dangerButtonClass}>
              {revokeBusy ? 'Working…' : 'Confirm'}
            </button>
            <button
              onClick={() => {
                setRevoking(null);
                setRevokeReason('');
              }}
              disabled={revokeBusy}
              className={subtleButtonClass}
            >
              Cancel
            </button>
          </div>
        )}

        {loading && offers.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Loading offers…</p>}

        {!loading && offers.length === 0 && !queryError && (
          <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 text-sm text-gray-600 dark:text-gray-300">
            <Tag size={16} className="mt-0.5 shrink-0 text-gray-400" />
            <span>
              No offer has been made to this organisation. An offer puts catalog products at negotiated prices in front of the customer, who accepts
              it from their subscription page or product dialog.{' '}
              {target
                ? 'This organisation already pays: an offer describes the whole new subscription and is applied to the running one in one click.'
                : 'With no paid subscription, accepting opens a Stripe page that saves a card and creates one — unless the offer carries payment terms, which is invoiced instead and takes billing details rather than a card.'}
            </span>
          </div>
        )}

        {offers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} busy={revokeBusy} onRevoke={setRevoking} target={target} />
        ))}
      </div>

      {isBuilderOpen && (
        <SubscriptionOfferModal
          organisationId={organisationId}
          organisationName={organisationName}
          target={target}
          seatUsage={seatUsage}
          onClose={() => setIsBuilderOpen(false)}
          onCreated={() => {
            setError('');
            setSuccess('Offer made — the account owners get a mail, and the offer is on their subscription page and in the product dialog.');
            void refetch();
          }}
        />
      )}
    </section>
  );
}
